import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import * as knowledgeApi from '@/services/api/knowledgeApi';
import type {
  KnowledgeArticle,
  DomainKnowledgeEntry,
  KnowledgeLintReport,
  CompilationStatus,
  GraphData,
  KnowledgeGap,
  KnowledgeArtifact,
  CompletenessResult,
} from '@/types/knowledge';

type ActiveTab = 'articles' | 'entries' | 'graph' | 'search' | 'lint' | 'feedback' | 'compile' | 'trends' | 'gaps' | 'research';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

export interface KnowledgeState {
  // Articles
  articles: KnowledgeArticle[];
  currentArticle: KnowledgeArticle | null;
  articleGraph: GraphData | null;
  articlesTotalPages: number;
  articlesPage: number;

  // Knowledge entries
  entries: DomainKnowledgeEntry[];
  entriesTotalPages: number;
  entriesPage: number;

  // Search
  searchResults: KnowledgeArticle[];

  // Compilation
  compilationStatus: CompilationStatus | null;
  isCompiling: boolean;

  // Lint
  latestLintReport: KnowledgeLintReport | null;
  lintReports: KnowledgeLintReport[];

  // Feedback
  pendingFeedback: DomainKnowledgeEntry[];

  // Gaps
  gaps: KnowledgeGap[];
  gapsTotalPages: number;
  gapsPage: number;

  // Completeness
  completeness: CompletenessResult | null;
  isAssessing: boolean;

  // Artifacts
  artifacts: KnowledgeArtifact[];

  // UI state
  activeTab: ActiveTab;
  searchQuery: string;
  selectedDomainTags: string[];
  isLoading: boolean;
  error: string | null;

  // Modal state
  isModalOpen: boolean;
  viewingArticleSlug: string | null;

  // Modal actions
  openModal: (tab?: ActiveTab) => void;
  closeModal: () => void;
  viewArticle: (slug: string) => void;
  backToList: () => void;

  // Actions
  fetchArticles: (params?: { page?: number; domain_tags?: string[]; status?: string }) => Promise<void>;
  deleteArticle: (articleId: string) => Promise<void>;
  fetchArticle: (id: string) => Promise<void>;
  fetchArticleBySlug: (slug: string) => Promise<void>;
  fetchArticleGraph: (domainTags?: string[]) => Promise<void>;
  fetchEntries: (params?: { page?: number }) => Promise<void>;
  searchKnowledge: (query: string, options?: { semantic?: boolean }) => Promise<void>;
  triggerCompilation: (domainTag: string) => Promise<void>;
  triggerCompileAll: () => Promise<void>;
  fetchCompilationStatus: () => Promise<void>;
  triggerLint: (domainTags?: string[]) => Promise<void>;
  fetchLatestLintReport: () => Promise<void>;
  fetchPendingFeedback: () => Promise<void>;
  approveFeedback: (id: string) => Promise<void>;
  rejectFeedback: (id: string) => Promise<void>;
  resolveLintFinding: (reportId: string, findingId: string) => Promise<void>;
  autoFixLintFinding: (reportId: string, findingId: string) => Promise<void>;
  fetchOpenGaps: (params?: { domain_tags?: string[] }) => Promise<void>;
  fetchAllGaps: (params?: { page?: number; status?: string; category?: string }) => Promise<void>;
  fillGap: (gapId: string, knowledgeEntryId: string) => Promise<void>;
  enrichGap: (gapId: string, knowledgeEntryId: string) => Promise<void>;
  deferGap: (gapId: string) => Promise<void>;
  markGapSufficient: (gapId: string) => Promise<void>;
  fetchCompleteness: () => Promise<void>;
  triggerCompletenessAssessment: (domainTags?: string[]) => Promise<void>;
  discoverNewGaps: (domainTags?: string[]) => Promise<void>;
  fetchArtifacts: (params?: { artifact_type?: string }) => Promise<void>;
  compileArtifacts: (domainTags: string[]) => Promise<void>;
  setActiveTab: (tab: ActiveTab) => void;
  setSearchQuery: (query: string) => void;
  setSelectedDomainTags: (tags: string[]) => void;
  clearError: () => void;
}

export const useKnowledgeStore = create<KnowledgeState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      articles: [],
      currentArticle: null,
      articleGraph: null,
      articlesTotalPages: 0,
      articlesPage: 1,
      entries: [],
      entriesTotalPages: 0,
      entriesPage: 1,
      searchResults: [],
      compilationStatus: null,
      isCompiling: false,
      latestLintReport: null,
      lintReports: [],
      pendingFeedback: [],
      gaps: [],
      gapsTotalPages: 0,
      gapsPage: 1,
      completeness: null,
      isAssessing: false,
      artifacts: [],
      activeTab: 'articles',
      searchQuery: '',
      selectedDomainTags: [],
      isLoading: false,
      error: null,
      isModalOpen: false,
      viewingArticleSlug: null,

      // Actions
      fetchArticles: async (params) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const result = await knowledgeApi.getArticles({
            page: params?.page || 1,
            limit: 20,
            domain_tags: params?.domain_tags,
            status: params?.status,
          });
          set((state) => {
            state.articles = result.data;
            state.articlesTotalPages = result.totalPages;
            state.articlesPage = result.page;
            state.isLoading = false;
          });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch articles'); state.isLoading = false; });
        }
      },

      deleteArticle: async (articleId) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          await knowledgeApi.deleteArticle(articleId);
          set((state) => {
            state.articles = state.articles.filter(a => a.article_id !== articleId);
            if (state.currentArticle?.article_id === articleId) {
              state.currentArticle = null;
              state.viewingArticleSlug = null;
            }
            state.isLoading = false;
          });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to delete article'); state.isLoading = false; });
        }
      },

      fetchArticle: async (id) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const article = await knowledgeApi.getArticle(id);
          set((state) => { state.currentArticle = article; state.isLoading = false; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch article'); state.isLoading = false; });
        }
      },

      fetchArticleBySlug: async (slug) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const article = await knowledgeApi.getArticleBySlug(slug);
          set((state) => { state.currentArticle = article; state.isLoading = false; });
          // Record view
          knowledgeApi.recordArticleView(article.article_id).catch(() => {});
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch article'); state.isLoading = false; });
        }
      },

      fetchArticleGraph: async (domainTags) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const graph = await knowledgeApi.getArticleGraph(domainTags);
          set((state) => { state.articleGraph = graph; state.isLoading = false; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch graph'); state.isLoading = false; });
        }
      },

      fetchEntries: async (params) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const result = await knowledgeApi.getKnowledgeEntries({
            page: params?.page || 1,
            limit: 20,
          });
          set((state) => {
            state.entries = result.data;
            state.entriesTotalPages = result.totalPages;
            state.entriesPage = result.page;
            state.isLoading = false;
          });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch entries'); state.isLoading = false; });
        }
      },

      searchKnowledge: async (query, options) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const results = await knowledgeApi.searchArticles(query, options);
          set((state) => { state.searchResults = results; state.isLoading = false; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Search failed'); state.isLoading = false; });
        }
      },

      triggerCompilation: async (domainTag) => {
        set((state) => { state.isCompiling = true; state.error = null; });
        try {
          await knowledgeApi.triggerCompilation(domainTag);
          await get().fetchCompilationStatus();
          await get().fetchArticles();
          set((state) => { state.isCompiling = false; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Compilation failed'); state.isCompiling = false; });
        }
      },

      triggerCompileAll: async () => {
        set((state) => { state.isCompiling = true; state.error = null; });
        try {
          await knowledgeApi.triggerCompileAll();
          await get().fetchCompilationStatus();
          await get().fetchArticles();
          set((state) => { state.isCompiling = false; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Compilation failed'); state.isCompiling = false; });
        }
      },

      fetchCompilationStatus: async () => {
        try {
          const status = await knowledgeApi.getCompilationStatus();
          set((state) => { state.compilationStatus = status; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch status'); });
        }
      },

      triggerLint: async (domainTags) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const report = await knowledgeApi.triggerLint(domainTags);
          set((state) => { state.latestLintReport = report; state.isLoading = false; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Lint failed'); state.isLoading = false; });
        }
      },

      fetchLatestLintReport: async () => {
        try {
          const report = await knowledgeApi.getLatestLintReport();
          set((state) => { state.latestLintReport = report; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch lint report'); });
        }
      },

      fetchPendingFeedback: async () => {
        try {
          const result = await knowledgeApi.getRecentFeedback();
          set((state) => { state.pendingFeedback = result.data; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch feedback'); });
        }
      },

      approveFeedback: async (id) => {
        try {
          await knowledgeApi.approveFeedback(id);
          set((state) => {
            state.pendingFeedback = state.pendingFeedback.filter(f => f.knowledge_id !== id);
          });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to approve'); });
        }
      },

      rejectFeedback: async (id) => {
        try {
          await knowledgeApi.rejectFeedback(id);
          set((state) => {
            state.pendingFeedback = state.pendingFeedback.filter(f => f.knowledge_id !== id);
          });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to reject'); });
        }
      },

      resolveLintFinding: async (reportId, findingId) => {
        try {
          await knowledgeApi.resolveLintFinding(reportId, findingId);
          await get().fetchLatestLintReport();
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to resolve finding'); });
        }
      },

      autoFixLintFinding: async (reportId, findingId) => {
        try {
          await knowledgeApi.autoFixLintFinding(reportId, findingId);
          await get().fetchLatestLintReport();
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to auto-fix'); });
        }
      },

      fetchOpenGaps: async (params) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const gaps = await knowledgeApi.getOpenGaps(params);
          set((state) => { state.gaps = gaps; state.isLoading = false; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch gaps'); state.isLoading = false; });
        }
      },

      fetchAllGaps: async (params) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const result = await knowledgeApi.getAllGaps({
            page: params?.page || 1,
            limit: 20,
            status: params?.status,
            category: params?.category,
          });
          set((state) => {
            state.gaps = result.data;
            state.gapsTotalPages = result.totalPages;
            state.gapsPage = result.page;
            state.isLoading = false;
          });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch gaps'); state.isLoading = false; });
        }
      },

      fillGap: async (gapId, knowledgeEntryId) => {
        try {
          await knowledgeApi.fillGap(gapId, knowledgeEntryId);
          set((state) => {
            state.gaps = state.gaps.filter(g => g.gap_id !== gapId);
          });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fill gap'); });
        }
      },

      enrichGap: async (gapId, knowledgeEntryId) => {
        try {
          await knowledgeApi.enrichGap(gapId, knowledgeEntryId);
          await get().fetchOpenGaps();
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to enrich gap'); });
        }
      },

      deferGap: async (gapId) => {
        try {
          await knowledgeApi.deferGap(gapId);
          set((state) => {
            state.gaps = state.gaps.filter(g => g.gap_id !== gapId);
          });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to defer gap'); });
        }
      },

      markGapSufficient: async (gapId) => {
        try {
          await knowledgeApi.markGapSufficient(gapId);
          set((state) => {
            state.gaps = state.gaps.filter(g => g.gap_id !== gapId);
          });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to mark gap sufficient'); });
        }
      },

      fetchCompleteness: async () => {
        try {
          const result = await knowledgeApi.getCompleteness();
          set((state) => { state.completeness = result; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch completeness'); });
        }
      },

      triggerCompletenessAssessment: async (domainTags) => {
        set((state) => { state.isAssessing = true; state.error = null; });
        try {
          const result = await knowledgeApi.triggerCompletenessAssessment(domainTags);
          set((state) => { state.completeness = result; state.isAssessing = false; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Assessment failed'); state.isAssessing = false; });
        }
      },

      discoverNewGaps: async (domainTags) => {
        set((state) => { state.isAssessing = true; state.error = null; });
        try {
          const newGaps = await knowledgeApi.discoverNewGaps(domainTags);
          set((state) => {
            state.gaps = [...state.gaps, ...newGaps];
            state.isAssessing = false;
          });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Gap discovery failed'); state.isAssessing = false; });
        }
      },

      fetchArtifacts: async (params) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const artifacts = await knowledgeApi.getArtifacts(params);
          set((state) => { state.artifacts = artifacts; state.isLoading = false; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Failed to fetch artifacts'); state.isLoading = false; });
        }
      },

      compileArtifacts: async (domainTags) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const artifacts = await knowledgeApi.compileArtifacts(domainTags);
          set((state) => { state.artifacts = artifacts; state.isLoading = false; });
        } catch (error: unknown) {
          set((state) => { state.error = getErrorMessage(error, 'Artifact compilation failed'); state.isLoading = false; });
        }
      },

      openModal: (tab) => {
        set((state) => {
          state.isModalOpen = true;
          if (tab) state.activeTab = tab;
          state.viewingArticleSlug = null;
        });
        get().fetchArticles();
        get().fetchCompilationStatus();
      },
      closeModal: () => {
        set((state) => {
          state.isModalOpen = false;
          state.viewingArticleSlug = null;
          state.currentArticle = null;
        });
      },
      viewArticle: (slug) => {
        set((state) => { state.viewingArticleSlug = slug; });
        get().fetchArticleBySlug(slug);
      },
      backToList: () => {
        set((state) => {
          state.viewingArticleSlug = null;
          state.currentArticle = null;
        });
      },

      setActiveTab: (tab) => set((state) => { state.activeTab = tab; }),
      setSearchQuery: (query) => set((state) => { state.searchQuery = query; }),
      setSelectedDomainTags: (tags) => set((state) => { state.selectedDomainTags = tags; }),
      clearError: () => set((state) => { state.error = null; }),
    })),
  ),
);
